/**
 * Author: marco
 * Last updated: 2025-06-01
 */

import { IDataObject } from 'n8n-workflow';

/**
 * Utility functions for InNotes N8N node
 */

/**
 * Clean undefined values from an object
 */
export function cleanObject(obj: IDataObject): IDataObject {
	const cleaned: IDataObject = {};
	for (const [key, value] of Object.entries(obj)) {
		if (value !== undefined && value !== null && value !== '') {
			cleaned[key] = value;
		}
	}
	return cleaned;
}

/**
 * Convert query parameters to URL search params
 */
export function buildQueryString(params: IDataObject): string {
	const cleanedParams = cleanObject(params);
	const searchParams = new URLSearchParams();

	for (const [key, value] of Object.entries(cleanedParams)) {
		if (Array.isArray(value)) {
			value.forEach(item => searchParams.append(key, String(item)));
		} else {
			searchParams.append(key, String(value));
		}
	}

	return searchParams.toString();
}

/**
 * Validate required fields are present
 */
export function validateRequiredFields(data: IDataObject, requiredFields: string[]): void {
	const missingFields = requiredFields.filter(field => !data[field]);
	if (missingFields.length > 0) {
		throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
	}
}

/**
 * Format date for API requests
 */
export function formatDate(date: Date | string): string {
	if (typeof date === 'string') {
		date = new Date(date);
	}
	return date.toISOString();
}

/**
 * Parse pagination parameters
 */
export function parsePaginationParams(limit?: number, offset?: number) {
	return {
		limit: limit ?? 50,
		offset: offset ?? 0,
	};
}

/**
 * Build request options for HTTP calls
 */
export function buildRequestOptions(
	method: string,
	url: string,
	data?: IDataObject,
	params?: IDataObject,
) {
	const options: IDataObject = {
		method,
		url,
		headers: {
			'Content-Type': 'application/json',
		},
	};

	if (data) {
		options.body = cleanObject(data);
	}

	if (params) {
		const queryString = buildQueryString(params);
		if (queryString) {
			options.url = `${url}?${queryString}`;
		}
	}

	return options;
}

/**
 * Extract error message from API response
 */
export function extractErrorMessage(error: any): string {
	if (error.response?.body?.message) {
		return error.response.body.message;
	}
	if (error.response?.body?.error) {
		return error.response.body.error;
	}
	if (error.message) {
		return error.message;
	}
	return 'Unknown error occurred';
}

/**
 * Transform InNotes contact data for output
 */
export function transformContactData(contact: IDataObject): IDataObject {
	return {
		id: contact.id,
		name: contact.name,
		email: contact.email,
		phone: contact.phone,
		linkedinUrl: contact.linkedinUrl,
		company: contact.company,
		position: contact.position,
		location: contact.location,
		tags: contact.tags ?? [],
		status: contact.status,
		notes: contact.notes ?? [],
		createdAt: contact.createdAt,
		updatedAt: contact.updatedAt,
	};
}

/**
 * Transform InNotes note data for output
 */
export function transformNoteData(note: IDataObject): IDataObject {
	return {
		id: note.id,
		contactId: note.contactId,
		content: note.content,
		type: note.type,
		tags: note.tags ?? [],
		attachments: note.attachments ?? [],
		createdAt: note.createdAt,
		updatedAt: note.updatedAt,
	};
}

/**
 * Transform InNotes job data for output
 */
export function transformJobData(job: IDataObject): IDataObject {
	return {
		id: job.id,
		title: job.title,
		company: job.company,
		description: job.description,
		url: job.url,
		status: job.status,
		salaryRange: job.salaryRange,
		location: job.location,
		type: job.type,
		contacts: job.contacts ?? [],
		notes: job.notes ?? [],
		createdAt: job.createdAt,
		updatedAt: job.updatedAt,
	};
}
